/* The deterministic gates. Every pass output — model or engine — goes through
   these before it is stored or handed to the next pass. They are the same in
   both apps and in the Edge Function; do not fork them.

   1. Validates against narrative-unit.schema.json.
   2. Every finding carries a source sentence, and that sentence occurs in the
      corpus when the corpus is supplied.
   3. Confidence is ordinal — the schema enforces the enum; this also catches a
      numeric `confidence` smuggled in anywhere in the tree.
   4. Feedback loops close structurally or are recorded as open chains.
   5. Every governed term is in the release vocabulary; anything else routes
      to Evolution and is never invented.  */

import { Ajv, type ErrorObject } from 'ajv';
import * as FormatsNS from 'ajv-formats';

// ajv-formats is CommonJS; the default export lands differently under Node,
// Vite and Deno. Resolve it once, portably.
type FormatsFn = (ajv: Ajv) => Ajv;
const addFormats: FormatsFn = (((FormatsNS as unknown as { default?: FormatsFn & { default?: FormatsFn } }).default?.default)
  ?? (FormatsNS as unknown as { default?: FormatsFn }).default
  ?? (FormatsNS as unknown as FormatsFn));
import schema from '../narrative-unit.schema.json' with { type: 'json' };
import type { NarrativeUnit, Evidence } from './types.generated.ts';

export type GateId = 'schema' | 'evidence' | 'ordinal' | 'closure' | 'vocabulary';

export interface GateFailure {
  gate: GateId;
  path: string;
  message: string;
  /** True when applyFixes() can repair it without inventing anything. */
  fixable: boolean;
}

export interface VocabularyTerm { term: string; kind: string; aliases?: string[] }

export interface GateContext {
  /** Source text by source name. When given, quotes must occur verbatim (whitespace-insensitive). */
  corpus?: Record<string, string>;
  /** The pinned release's vocabulary. When omitted, gate 5 is skipped and reported as such. */
  vocabulary?: VocabularyTerm[];
  /** Which layers to check. Default: all. Per-pass gating passes the layer just produced. */
  layers?: LayerKey[];
}

export type LayerKey = 'meaning' | 'landscape' | 'architecture' | 'pathways' | 'emerging_issues' | 'transformation_opportunities' | 'dark_spots';
export const LAYERS: LayerKey[] = ['meaning', 'landscape', 'architecture', 'pathways', 'emerging_issues', 'transformation_opportunities', 'dark_spots'];

export interface GateReport {
  ok: boolean;
  failures: GateFailure[];
  skipped: GateId[];
}

const ajv = new Ajv({ allErrors: true, strict: false });
addFormats(ajv);
const validateSchema = ajv.compile(schema as object);

const norm = (s: string) => s.replace(/\s+/g, ' ').trim().toLowerCase();

function describe(e: ErrorObject): string {
  return `${e.instancePath || '/'} ${e.message ?? ''}${e.params && 'allowedValues' in e.params ? ` (${(e.params as { allowedValues: string[] }).allowedValues.join(' | ')})` : ''}`.trim();
}

/** Collect every finding in the unit with its JSON path, for gates 2 and 5. */
function findings(unit: NarrativeUnit, layers: LayerKey[]): { path: string; layer: LayerKey; evidence?: Evidence; item: unknown }[] {
  const out: { path: string; layer: LayerKey; evidence?: Evidence; item: unknown }[] = [];
  const has = (l: LayerKey) => layers.includes(l);
  if (has('meaning')) unit.meaning.frames.forEach((f, i) => out.push({ path: `/meaning/frames/${i}`, layer: 'meaning', evidence: f.evidence, item: f }));
  if (has('landscape')) {
    unit.landscape.actors.forEach((a, i) => out.push({ path: `/landscape/actors/${i}`, layer: 'landscape', evidence: a.evidence, item: a }));
    unit.landscape.absent.forEach((a, i) => out.push({ path: `/landscape/absent/${i}`, layer: 'landscape', evidence: a.evidence, item: a }));
  }
  if (has('architecture')) {
    unit.architecture.invisible_architectures.forEach((a, i) => out.push({ path: `/architecture/invisible_architectures/${i}`, layer: 'architecture', evidence: a.evidence, item: a }));
    unit.architecture.feedback_loops.forEach((l, i) => out.push({ path: `/architecture/feedback_loops/${i}`, layer: 'architecture', evidence: l.evidence, item: l }));
  }
  if (has('pathways')) unit.pathways.items.forEach((p, i) => out.push({ path: `/pathways/items/${i}`, layer: 'pathways', evidence: p.evidence, item: p }));
  if (has('emerging_issues')) unit.emerging_issues.items.forEach((e, i) => out.push({ path: `/emerging_issues/items/${i}`, layer: 'emerging_issues', evidence: e.evidence, item: e }));
  if (has('transformation_opportunities')) unit.transformation_opportunities.items.forEach((o, i) => out.push({ path: `/transformation_opportunities/items/${i}`, layer: 'transformation_opportunities', evidence: o.evidence, item: o }));
  return out;
}

function walkNumericConfidence(node: unknown, path: string, out: GateFailure[]) {
  if (!node || typeof node !== 'object') return;
  if (Array.isArray(node)) { node.forEach((n, i) => walkNumericConfidence(n, `${path}/${i}`, out)); return; }
  for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
    if (k === 'confidence' && typeof v !== 'string') out.push({ gate: 'ordinal', path: `${path}/${k}`, message: `confidence must be high | medium | low, got ${JSON.stringify(v)}`, fixable: false });
    else if (/^(score|probability|likelihood)$/i.test(k) && typeof v === 'number') out.push({ gate: 'ordinal', path: `${path}/${k}`, message: `numeric ${k} is not permitted on a finding`, fixable: true });
    walkNumericConfidence(v, `${path}/${k}`, out);
  }
}

/** Key term of a loop endpoint, mirroring the engine's closure heuristic. */
function keyTerm(s: string): string | null {
  const m = s.toLowerCase().match(/[a-z]{5,}/g);
  if (!m) return null;
  const stop = new Set(['which', 'there', 'their', 'would', 'about', 'because', 'those', 'these', 'other', 'where', 'while', 'being', 'after', 'before', 'could', 'should']);
  return m.find(w => !stop.has(w)) ?? null;
}

/** Recompute closure from the loop graph: an edge closes only if another edge's head contains its tail's key term. */
export function closureOf(loops: NarrativeUnit['architecture']['feedback_loops']): boolean[] {
  const edges = loops.map(l => { const [head = '', tail = ''] = l.name.split('→').map(x => x.trim()); return { head: head.toLowerCase(), tail }; });
  return edges.map((e, i) => {
    const k = keyTerm(e.tail);
    return !!k && edges.some((o, j) => j !== i && o.head.includes(k));
  });
}

export function runGates(unit: NarrativeUnit, ctx: GateContext = {}): GateReport {
  const failures: GateFailure[] = [];
  const skipped: GateId[] = [];
  const layers = ctx.layers ?? LAYERS;

  // 1 — schema. When gating one pass, only that layer's failures count;
  // an earlier layer's problem is that pass's record, not this one's.
  if (!validateSchema(unit)) {
    const scoped = ctx.layers ? (p: string) => layers.some(l => p === `/${l}` || p.startsWith(`/${l}/`)) : () => true;
    for (const e of validateSchema.errors ?? []) {
      const path = e.instancePath || '/';
      if (scoped(path)) failures.push({ gate: 'schema', path, message: describe(e), fixable: false });
    }
  }

  // 2 — evidence
  const corpusNorm = ctx.corpus ? Object.fromEntries(Object.entries(ctx.corpus).map(([k, v]) => [k, norm(v)])) : null;
  const allCorpus = corpusNorm ? Object.values(corpusNorm).join(' \n ') : null;
  for (const f of findings(unit, layers)) {
    if (!f.evidence || !f.evidence.quote?.trim()) { failures.push({ gate: 'evidence', path: f.path, message: 'finding carries no source sentence', fixable: true }); continue; }
    if (allCorpus) {
      const q = norm(f.evidence.quote).replace(/…$/, '');
      const inNamed = corpusNorm?.[f.evidence.source]?.includes(q);
      if (!inNamed && !allCorpus.includes(q)) failures.push({ gate: 'evidence', path: `${f.path}/evidence`, message: `quoted sentence does not occur in the sources`, fixable: true });
    }
  }
  if (layers.includes('dark_spots') && unit.dark_spots.length === 0) {
    failures.push({ gate: 'evidence', path: '/dark_spots', message: 'dark_spots is empty: silence must be stated, not omitted', fixable: true });
  }

  // 3 — ordinal
  walkNumericConfidence(unit, '', failures);

  // 4 — closure
  if (layers.includes('architecture')) {
    const closes = closureOf(unit.architecture.feedback_loops);
    unit.architecture.feedback_loops.forEach((l, i) => {
      if (l.closes && !closes[i]) failures.push({ gate: 'closure', path: `/architecture/feedback_loops/${i}/closes`, message: `loop "${l.name}" is marked closed but no detected edge begins where it ends`, fixable: true });
    });
  }

  // 5 — vocabulary
  if (!ctx.vocabulary) skipped.push('vocabulary');
  else {
    const byKind = (kind: string) => new Set(ctx.vocabulary!.filter(t => t.kind === kind).flatMap(t => [t.term, ...(t.aliases ?? [])]).map(norm));
    const archs = byKind('invisible_architecture'), types = byKind('pathway_type'), logics = byKind('steering_logic');
    if (layers.includes('architecture')) unit.architecture.invisible_architectures.forEach((a, i) => {
      if (!archs.has(norm(a.name))) failures.push({ gate: 'vocabulary', path: `/architecture/invisible_architectures/${i}`, message: `"${a.name}" is not an invisible_architecture term in this release`, fixable: true });
    });
    if (layers.includes('pathways')) unit.pathways.items.forEach((p, i) => {
      if (p.typology !== null && !types.has(norm(p.typology))) failures.push({ gate: 'vocabulary', path: `/pathways/items/${i}/typology`, message: `"${p.typology}" is not a pathway_type term in this release`, fixable: true });
    });
    if (layers.includes('transformation_opportunities')) unit.transformation_opportunities.items.forEach((o, i) => {
      if (o.steering_logic !== null && !logics.has(norm(o.steering_logic))) failures.push({ gate: 'vocabulary', path: `/transformation_opportunities/items/${i}/steering_logic`, message: `"${o.steering_logic}" is not a steering_logic term in this release`, fixable: true });
    });
  }

  return { ok: failures.length === 0, failures, skipped };
}

/** Repairs what can be repaired without inventing: drops findings that cannot
    cite, opens loops that do not close, routes unknown terms to Evolution,
    strips numeric scores, and states an empty dark-spots list as a silence.
    Returns the fixed unit and the notes describing each repair. */
export function applyFixes(unit: NarrativeUnit, report: GateReport): { unit: NarrativeUnit; notes: string[] } {
  const u: NarrativeUnit = JSON.parse(JSON.stringify(unit));
  const notes: string[] = [];
  const drop = new Map<string, Set<number>>();
  const markDrop = (arrPath: string, i: number) => { if (!drop.has(arrPath)) drop.set(arrPath, new Set()); drop.get(arrPath)!.add(i); };

  for (const f of report.failures) {
    if (!f.fixable) continue;
    const m = f.path.match(/^\/(meaning\/frames|landscape\/actors|landscape\/absent|architecture\/invisible_architectures|architecture\/feedback_loops|pathways\/items|emerging_issues\/items|transformation_opportunities\/items)\/(\d+)(\/.*)?$/);
    if (f.gate === 'evidence' && f.path === '/dark_spots') {
      u.dark_spots = ['No structural silence was recorded for this reading — treat the unit as under-examined rather than complete.'];
      notes.push('dark_spots stated as a silence');
    } else if (f.gate === 'evidence' && m) {
      markDrop(m[1]!, Number(m[2])); notes.push(`dropped ${m[1]}[${m[2]}]: no citable sentence`);
    } else if (f.gate === 'closure' && m && m[1] === 'architecture/feedback_loops') {
      const l = u.architecture.feedback_loops[Number(m[2])]; if (l) { l.closes = false; notes.push(`loop "${l.name}" recorded as an open chain`); }
    } else if (f.gate === 'vocabulary' && m) {
      const i = Number(m[2]);
      if (m[1] === 'architecture/invisible_architectures') {
        const a = u.architecture.invisible_architectures[i];
        if (a) { u.evolution.push({ term: a.name, kind: 'invisible_architecture', layer: 'architecture', evidence: a.evidence }); markDrop(m[1], i); notes.push(`"${a.name}" routed to Evolution`); }
      } else if (m[1] === 'pathways/items') {
        const p = u.pathways.items[i];
        if (p && p.typology) { u.evolution.push({ term: p.typology, kind: 'pathway_type', layer: 'pathways', evidence: p.evidence }); notes.push(`typology "${p.typology}" routed to Evolution; pathway left untyped`); p.typology = null; }
      } else if (m[1] === 'transformation_opportunities/items') {
        const o = u.transformation_opportunities.items[i];
        if (o && o.steering_logic) { u.evolution.push({ term: o.steering_logic, kind: 'steering_logic', layer: 'transformation_opportunities', evidence: o.evidence }); notes.push(`steering logic "${o.steering_logic}" routed to Evolution`); o.steering_logic = null; }
      }
    } else if (f.gate === 'ordinal') {
      const segs = f.path.split('/').filter(Boolean); const key = segs.pop()!;
      let node: unknown = u; for (const s of segs) node = (node as Record<string, unknown>)?.[s];
      if (node && typeof node === 'object') { delete (node as Record<string, unknown>)[key]; notes.push(`removed numeric ${key} at ${f.path}`); }
    }
  }
  for (const [arrPath, idx] of drop) {
    const segs = arrPath.split('/'); let node: unknown = u; for (const s of segs.slice(0, -1)) node = (node as Record<string, unknown>)[s];
    const key = segs.at(-1)!; const arr = (node as Record<string, unknown[]>)[key]!;
    (node as Record<string, unknown[]>)[key] = arr.filter((_, i) => !idx.has(i));
  }
  return { unit: u, notes };
}

/** Convenience: gate, fix what is fixable, gate again. */
export function gateAndFix(unit: NarrativeUnit, ctx: GateContext = {}): { unit: NarrativeUnit; report: GateReport; notes: string[]; status: 'passed' | 'retried' | 'failed' } {
  const first = runGates(unit, ctx);
  if (first.ok) return { unit, report: first, notes: [], status: 'passed' };
  const { unit: fixed, notes } = applyFixes(unit, first);
  const second = runGates(fixed, ctx);
  return { unit: fixed, report: second, notes, status: second.ok ? 'retried' : 'failed' };
}
